import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getOpenOrbPanels, ORB_PANEL_SELECTOR, positionBesideOrbPanels } from '../orbPanels';

export default function useOrbPanelPosition(options) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const panelsRef = useRef([]);
  const [panelMovement, setPanelMovement] = useState(false);
  const [displaced, setDisplaced] = useState(false);

  const syncPosition = useCallback((force = false) => {
    const panels = getOpenOrbPanels(window.innerWidth);
    const previous = panelsRef.current;
    const changed = panels.length !== previous.length || panels.some((panel, index) => {
      const old = previous[index];
      return panel.element !== old.element || panel.side !== old.side || panel.edge !== old.edge;
    });
    panelsRef.current = panels;
    if (!changed && !force) return;
    const { getPreferredPosition, getPosition, isDragging, moveTo } = optionsRef.current;
    if (isDragging()) return;
    const preferred = getPreferredPosition();
    const target = positionBesideOrbPanels(preferred, panels, window.innerWidth);
    const current = getPosition();
    setDisplaced(target.x !== preferred.x);
    if (target.x === current.x && target.y === current.y) return;
    setPanelMovement(true);
    moveTo(target);
  }, []);

  const stopPanelMovement = useCallback(() => setPanelMovement(false), []);

  useLayoutEffect(() => {
    const sizes = new ResizeObserver(() => syncPosition());
    const observed = new Set();
    const panelAttributes = new MutationObserver(() => syncPosition());
    const discoverPanels = () => {
      const elements = Array.from(document.querySelectorAll(ORB_PANEL_SELECTOR));
      if (elements.length !== observed.size || elements.some((element) => !observed.has(element))) {
        sizes.disconnect();
        panelAttributes.disconnect();
        observed.clear();
        elements.forEach((element) => {
          observed.add(element);
          sizes.observe(element);
          panelAttributes.observe(element, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
        });
      }
      syncPosition();
    };
    discoverPanels();
    const additions = new MutationObserver(discoverPanels);
    additions.observe(document.body, { subtree: true, childList: true });
    return () => {
      additions.disconnect();
      panelAttributes.disconnect();
      sizes.disconnect();
    };
  }, [syncPosition]);

  return { panelMovement, displaced, syncPosition, stopPanelMovement };
}
